import React, { useEffect, useState } from 'react';
import { Paper, Box, Typography, Chip, CircularProgress, Stack, Link } from '@mui/material';
import { AdminSmallBusinessService, Company, SmallBusiness } from 'us-web-services';
import PageStyles from 'src/styles/PageStyles';

interface Props {
  company: Company;
}

function statusColor(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'success';
    case 'past_due':
    case 'incomplete':
      return 'warning';
    case 'canceled':
    case 'unpaid':
      return 'error';
    default:
      return 'default';
  }
}

function formatDate(value: string | null): string {
  if (!value) return '—';

  return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function CompanySubscription({ company }: Props) {
  const { classes } = PageStyles();
  const [data, setData] = useState<SmallBusiness>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchSubscription = async () => {
      setLoading(true);
      try {
        const response = await AdminSmallBusinessService.findByCompanyId(company.id);

        if (!cancelled) setData(response?.data ?? undefined);
      } catch {
        if (!cancelled) setData(undefined);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSubscription();

    return () => {
      cancelled = true;
    };
  }, [company.id]);

  return (
    <Paper className={classes.paper}>
      <Typography variant='h6'>Small Business Subscription</Typography>

      {loading && (
        <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {!loading && !data && (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          No SMB subscription on file for this company.
        </Typography>
      )}

      {!loading && data && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='body1'>
              <strong>{data.tier}</strong> · {data.billingInterval}
            </Typography>
            <Chip size='small' color={statusColor(data.status)} label={data.status.replace('_', ' ')} />
          </Stack>
          <Typography variant='body2'>
            Seats: {data.seatsUsed} / {data.seatLimit}
          </Typography>
          <Typography variant='body2'>MRR: {formatMoney(data.mrrCents)}</Typography>
          <Typography variant='body2'>Next bill: {formatDate(data.currentPeriodEnd)}</Typography>
          <Typography variant='body2' color='text.secondary'>
            Started: {formatDate(data.created)}
          </Typography>
          <Link
            href={`https://dashboard.stripe.com/customers?query=companyId%3A${data.companyId}`}
            target='_blank'
            rel='noopener noreferrer'
            sx={{ alignSelf: 'flex-start', mt: 1 }}
          >
            Open in Stripe Dashboard →
          </Link>
        </Stack>
      )}
    </Paper>
  );
}

export default CompanySubscription;
